﻿import { create } from "zustand";

const load = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };
const save = (k, v) => localStorage.setItem(k, JSON.stringify(v));
const uid = () => Math.random().toString(36).slice(2, 11);

export const DEFAULT_SETTINGS = {
    theme: "dark",
    pinEnabled: true,
    standaloneBehavior: true,
    keyboardShortcuts: true,
    rightClickMenu: true,
    snap: true,
    snapStep: 0.5,
    gridVisible: true,
    units: "mm",
    printBed: { x: 220, y: 220, z: 250 },   // max print dimensions (mm)
    showPrintBed: true,
    fallbackOrder: ["default", "openai", "anthropic", "groq"],
};

/* ---------- Projects persisted ---------- */
const initialProjects = load("roocad_projects", null);
const seedProject = { id: uid(), name: "Untitled Project", model: [], updatedAt: Date.now() };
const projects = initialProjects && initialProjects.length ? initialProjects : [seedProject];
const activeProjectId = load("roocad_active_project", projects[0].id);
const activeProject = projects.find((p) => p.id === activeProjectId) || projects[0];
const persistProjects = (list, activeId) => {
    save("roocad_projects", list);
    if (activeId) save("roocad_active_project", activeId);
};

export const useCadStore = create((set, get) => ({
    /* ---------- core ---------- */
    prompt: "",
    isResearching: false,
    aiStatus: "idle",
    currentModel: activeProject.model,
    history: [],
    future: [],
    selectedPartId: null,
    mode: "simple",
    device: "desktop",
    activeProvider: "default",
    isStandalone: false,
    locked: (load("roocad_settings", {}).pinEnabled !== false) ? !sessionStorage.getItem("roocad_unlocked") : false,
    settings: { ...DEFAULT_SETTINGS, ...load("roocad_settings", {}) },
    apiKeys: load("roocad_api_keys", {}),
    apiModels: load("roocad_api_models", {}),
    projects,
    activeProjectId: activeProject.id,
    savedShapes: load("roocad_saved_shapes", []),
    aiFeedback: load("roocad_ai_feedback", []),

    /* ---------- projects ---------- */
    newProject: (name) => set((s) => {
        const proj = { id: uid(), name: name || `Project ${s.projects.length + 1}`, model: [], updatedAt: Date.now() };
        const list = [...s.projects.map((p) => ({ ...p, model: p.id === s.activeProjectId ? s.currentModel : p.model })), proj];
        persistProjects(list, proj.id);
        return { projects: list, activeProjectId: proj.id, currentModel: [], history: [], future: [], selectedPartId: null };
    }),
    switchProject: (id) => set((s) => {
        const updated = s.projects.map((p) => p.id === s.activeProjectId ? { ...p, model: s.currentModel, updatedAt: Date.now() } : p);
        const target = updated.find((p) => p.id === id);
        if (!target) return {};
        persistProjects(updated, id);
        return { projects: updated, activeProjectId: id, currentModel: target.model || [], history: [], future: [], selectedPartId: null };
    }),
    deleteProject: (id) => set((s) => {
        if (s.projects.length <= 1) return {};
        let list = s.projects.filter((p) => p.id !== id);
        let current = s.currentModel, active = s.activeProjectId;
        if (id === s.activeProjectId) { active = list[0].id; current = list[0].model || []; }
        persistProjects(list, active);
        return { projects: list, activeProjectId: active, currentModel: current, history: [], future: [], selectedPartId: null };
    }),
    renameProject: (id, name) => set((s) => {
        const list = s.projects.map((p) => p.id === id ? { ...p, name } : p);
        persistProjects(list, s.activeProjectId);
        return { projects: list };
    }),

    /* ---------- saved shape library ---------- */
    saveShape: (name) => set((s) => {
        if (!s.currentModel.length) return {};
        const shape = { id: uid(), name: name || `Shape ${s.savedShapes.length + 1}`, parts: JSON.parse(JSON.stringify(s.currentModel)), createdAt: Date.now() };
        const savedShapes = [...s.savedShapes, shape];
        save("roocad_saved_shapes", savedShapes);
        return { savedShapes };
    }),
    loadShape: (id) => set((s) => {
        const shape = s.savedShapes.find((x) => x.id === id);
        if (!shape) return {};
        const parts = shape.parts.map((p) => ({ ...p, id: uid() }));
        return { history: [...s.history, s.currentModel], future: [], currentModel: [...s.currentModel, ...parts] };
    }),
    deleteShape: (id) => set((s) => {
        const savedShapes = s.savedShapes.filter((x) => x.id !== id);
        save("roocad_saved_shapes", savedShapes);
        return { savedShapes };
    }),

    /* ---------- AI self-learning loop ---------- */
    logFeedback: (prompt, correction) => set((s) => {
        const aiFeedback = [...s.aiFeedback.slice(-49), { prompt, correction, at: Date.now() }];
        save("roocad_ai_feedback", aiFeedback);
        return { aiFeedback };
    }),
    clearFeedback: () => { save("roocad_ai_feedback", []); set({ aiFeedback: [] }); },

    unlock: () => { sessionStorage.setItem("roocad_unlocked", "1"); set({ locked: false }); },
    updateSetting: (key, value) => set((s) => {
        const settings = { ...s.settings, [key]: value };
        save("roocad_settings", settings);
        return { settings };
    }),
    setApiKey: (provider, key) => set((s) => {
        const apiKeys = { ...s.apiKeys, [provider]: key };
        save("roocad_api_keys", apiKeys);
        return { apiKeys };
    }),
    removeApiKey: (provider) => set((s) => {
        const apiKeys = { ...s.apiKeys }; delete apiKeys[provider];
        save("roocad_api_keys", apiKeys);
        return { apiKeys };
    }),
    setApiModel: (provider, model) => set((s) => {
        const apiModels = { ...s.apiModels, [provider]: model };
        save("roocad_api_models", apiModels);
        return { apiModels };
    }),

    /* ---------- basic setters ---------- */
    setPrompt: (prompt) => set({ prompt }),
    setAiStatus: (status) => set({ aiStatus: status }),
    setResearching: (val) => set({ isResearching: val }),
    setActiveProvider: (provider) => set({ activeProvider: provider }),
    setMode: (mode) => set({ mode }),
    setDevice: (device) => set({ device }),
    setStandalone: (isStandalone) => set({ isStandalone }),
    setSelectedPart: (id) => set({ selectedPartId: id }),
    unlock: () => { sessionStorage.setItem("roocad_unlocked", "1"); set({ locked: false }); },
    updateSetting: (key, value) => set((s) => {
        const settings = { ...s.settings, [key]: value };
        save("roocad_settings", settings);
        return { settings };
    }),
    setApiKey: (provider, key) => set((s) => {
        const apiKeys = { ...s.apiKeys, [provider]: key };
        save("roocad_api_keys", apiKeys);
        return { apiKeys };
    }),
    removeApiKey: (provider) => set((s) => {
        const apiKeys = { ...s.apiKeys }; delete apiKeys[provider];
        save("roocad_api_keys", apiKeys);
        return { apiKeys };
    }),
    setApiModel: (provider, model) => set((s) => {
        const apiModels = { ...s.apiModels, [provider]: model };
        save("roocad_api_models", apiModels);
        return { apiModels };
    }),

    /* ---------- model ops (with undo) ---------- */
    pushHistory: () => set((s) => ({ history: [...s.history.slice(-49), s.currentModel], future: [] })),
    setCurrentModel: (model) => set((s) => ({ history: [...s.history.slice(-49), s.currentModel], future: [], currentModel: model })),
    updatePart: (id, updates) => set((s) => ({ currentModel: s.currentModel.map((p) => p.id === id ? { ...p, ...updates } : p) })),
    removePart: (id) => set((s) => ({ currentModel: s.currentModel.filter((p) => p.id !== id) })),

    undo: () => set((s) => {
        if (!s.history.length) return {};
        const prev = s.history[s.history.length - 1];
        return { currentModel: prev, history: s.history.slice(0, -1), future: [...s.future, s.currentModel], selectedPartId: null };
    }),
    redo: () => set((s) => {
        if (!s.future.length) return {};
        const next = s.future[s.future.length - 1];
        return { currentModel: next, future: s.future.slice(0, -1), history: [...s.history, s.currentModel] };
    }),

    /* ---------- grouping & visibility ---------- */
    groupSelected: () => set((s) => {
        if (!s.selectedPartId) return {};
        const gid = uid();
        return { currentModel: s.currentModel.map((p) => p.id === s.selectedPartId ? { ...p, group: gid } : p) };
    }),
    groupAll: () => set((s) => {
        const gid = uid();
        return { currentModel: s.currentModel.map((p) => ({ ...p, group: gid })) };
    }),
    ungroupSelected: () => set((s) => ({
        currentModel: s.currentModel.map((p) => p.id === s.selectedPartId ? { ...p, group: null } : p),
    })),
    toggleVisibility: (id) => set((s) => ({
        currentModel: s.currentModel.map((p) => p.id === id ? { ...p, hidden: !p.hidden } : p),
    })),

    /* ---------- clipboard ---------- */
    clipboard: null,
    copySelected: () => set((s) => {
        const part = s.currentModel.find((p) => p.id === s.selectedPartId);
        if (part) return { clipboard: JSON.parse(JSON.stringify(part)) };
        return {};
    }),
    paste: () => set((s) => {
        if (!s.clipboard) return {};
        const copy = { ...s.clipboard, id: uid(), position: [s.clipboard.position[0] + 1, s.clipboard.position[1], s.clipboard.position[2] + 1] };
        return { history: [...s.history, s.currentModel], future: [], currentModel: [...s.currentModel, copy], selectedPartId: copy.id };
    }),
}));

export { uid, load, save };
